import { ChangeDetectionStrategy, Component, computed, inject, signal, TemplateRef, ViewChild, viewChild } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { tap } from 'rxjs/operators';
import { TuiButton, TuiDialogService, TuiLink, TuiNotificationService } from '@taiga-ui/core';
import { tuiDialog } from '@taiga-ui/core';
import { TuiDialogContext } from '@taiga-ui/core';
import { POLYMORPHEUS_CONTEXT, PolymorpheusTemplate } from '@taiga-ui/polymorpheus';
import { LoginRequest } from '../../models/login.model';
import { AuthService } from '../../services/auth.service';
import { RegisterDialogComponent } from '../register-dialog/register-dialog.component';
import { extractHttpErrorMessage } from '../../../../core/utils/alert-helper.util';
import { TurnstileWidgetComponent } from '../../../../shared/components/turnstile-widget/turnstile-widget.component';
import { environment } from '../../../../../environments/environment';

const EMAIL_NOT_VERIFIED_MESSAGE =
  'Your email address is not verified. Please check your inbox for the verification link.';

@Component({
  selector: 'app-login-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReactiveFormsModule, TuiButton, TuiLink, PolymorpheusTemplate, TurnstileWidgetComponent],
  templateUrl: './login-dialog.component.html',
  styleUrl: './login-dialog.component.less',
})
export class LoginDialogComponent {
  private fb = inject(FormBuilder);
  private authService = inject(AuthService);
  private alerts = inject(TuiNotificationService);
  private readonly dialogs = inject(TuiDialogService);
  private readonly context = inject<TuiDialogContext<void, void>>(POLYMORPHEUS_CONTEXT);

  @ViewChild('verificationAlertTemplate')
  private verificationAlertTemplate?: TemplateRef<unknown>;

  private readonly turnstileWidget = viewChild<TurnstileWidgetComponent>('turnstileWidget');

  private readonly registerDialog = tuiDialog(RegisterDialogComponent, {
    size: 'm',
    // Non-dismissible: the Cap widget resizes on click, so an overlay/Esc
    // dismiss can fire mid-interaction and drop the form. Close via the X.
    dismissible: false,
    label: 'Create Account',
  });

  loginForm = this.fb.group({
    username: ['', [Validators.required]],
    password: ['', [Validators.required, Validators.minLength(6)]],
  });

  forgotForm = this.fb.group({
    email: ['', [Validators.required, Validators.email]],
  });

  loading = signal(false);
  turnstileToken = signal<string | null>(null);
  // Site key configured locally AND backend admin toggle is on.
  readonly turnstileEnabled = computed(
    () => !!environment.turnstileSiteKey && this.authService.captchaEnabled(),
  );
  showForgotPassword = signal(false);
  forgotSent = signal(false);
  sendingVerification = signal(false);
  verificationSent = signal(false);

  get usernameControl() {
    return this.loginForm.get('username');
  }

  get passwordControl() {
    return this.loginForm.get('password');
  }

  get forgotEmailControl() {
    return this.forgotForm.get('email');
  }

  get submitDisabled(): boolean {
    if (this.loginForm.invalid || this.loading()) {
      return true;
    }
    if (this.turnstileEnabled() && !this.turnstileToken()) {
      return true;
    }
    return false;
  }

  get submitAppearance(): 'positive' | 'primary' {
    return this.submitDisabled ? 'primary' : 'positive';
  }

  onTurnstileToken(token: string): void {
    this.turnstileToken.set(token || null);
  }

  onTurnstileExpired(): void {
    this.turnstileToken.set(null);
  }

  onTurnstileError(errorCode: string): void {
    console.error('[LoginDialog] Turnstile error:', errorCode);
    this.turnstileToken.set(null);
  }

  onSubmit(): void {
    if (this.loginForm.valid) {
      this.loading.set(true);
      const credentials: LoginRequest = {
        username: this.loginForm.value.username || '',
        password: this.loginForm.value.password || '',
      };

      if (this.turnstileEnabled() && this.turnstileToken()) {
        credentials.turnstile_token = this.turnstileToken();
      }

      this.authService
        .login(credentials)
        .pipe(
          tap(() => {
            this.loading.set(false);
          })
        )
        .subscribe({
          next: () => {
            this.context.completeWith();
            this.alerts
              .open('Login successful', {
                label: 'Success',
                appearance: 'positive',
                autoClose: 3000,
              })
              .subscribe();
          },
          error: (error) => {
            this.loading.set(false);
            this.turnstileToken.set(null);
            this.turnstileWidget()?.reset();
            const message = extractHttpErrorMessage(error, 'Login failed. Please try again.');

            if (message === EMAIL_NOT_VERIFIED_MESSAGE && this.verificationAlertTemplate) {
              this.verificationSent.set(false);
              this.sendingVerification.set(false);
              this.alerts
                .open(this.verificationAlertTemplate, {
                  label: 'Error',
                  appearance: 'negative',
                  autoClose: 0,
                })
                .subscribe();
            } else {
              this.alerts
                .open(message, {
                  label: 'Error',
                  appearance: 'negative',
                  autoClose: 0,
                })
                .subscribe();
            }
          },
        });
    }
  }

  openForgotPassword(): void {
    this.showForgotPassword.set(true);
    this.forgotSent.set(false);
    this.forgotForm.reset();
  }

  backToLogin(): void {
    this.showForgotPassword.set(false);
  }

  submitForgotPassword(): void {
    if (this.forgotForm.invalid) {
      this.forgotForm.markAllAsTouched();
      return;
    }

    this.loading.set(true);
    const email = this.forgotForm.value.email || '';

    this.authService.forgotPassword(email).subscribe({
      next: () => {
        this.loading.set(false);
        this.forgotSent.set(true);
      },
      error: () => {
        this.loading.set(false);
        // Show success anyway to not reveal whether email exists
        this.forgotSent.set(true);
      },
    });
  }

  resendVerificationEmail(): void {
    if (this.sendingVerification()) return;

    const username = this.loginForm.value.username || '';
    if (!username) return;

    this.sendingVerification.set(true);
    this.authService.resendVerifyEmail(username).subscribe({
      next: () => {
        this.sendingVerification.set(false);
        this.verificationSent.set(true);
      },
      error: () => {
        this.sendingVerification.set(false);
        this.alerts
          .open('Failed to resend verification email. Please try again later.', {
            label: 'Error',
            appearance: 'negative',
            autoClose: 0,
          })
          .subscribe();
      },
    });
  }

  openRegisterDialog(): void {
    this.context.completeWith();
    this.registerDialog().subscribe();
  }
}
